import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Activity, Loader2, RotateCw } from "lucide-react";

// Per-tenant daily login sparkline. Reads /admin/analytics/logins?days=N
// and renders a lightweight inline SVG sparkline (no chart library needed —
// keeps the row compact and avoids re-mounting hundreds of Recharts trees).
export default function SuperAdminLoginMetrics() {
  const [data, setData] = useState(null); // { days, series, day_labels }
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [showDormantOnly, setShowDormantOnly] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/analytics/logins", { params: { days } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load login metrics");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const rows = useMemo(() => {
    if (!data?.series) return [];
    let arr = Object.values(data.series);
    if (showDormantOnly) arr = arr.filter((s) => s.dormant);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      arr = arr.filter((s) => (s.tenant_name || "").toLowerCase().includes(needle));
    }
    // Most-active tenants first, dormant tenants sink to the bottom
    arr.sort((a, b) => (b.total - a.total) || (a.tenant_name || "").localeCompare(b.tenant_name || ""));
    return arr;
  }, [data, showDormantOnly, q]);

  const totalLogins = rows.reduce((n, r) => n + r.total, 0);
  const activeTenants = rows.filter((r) => !r.dormant).length;

  return (
    <div className="ll-card-flat overflow-hidden" data-testid="sa-login-metrics-card">
      <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
        <div className="flex items-center gap-2">
          <Activity size={14} />
          <div className="font-display font-bold">Login activity</div>
          <div className="text-xs text-[var(--ll-ink-soft)]">{totalLogins} logins · {activeTenants} active tenants</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter tenants…"
            data-testid="sa-login-filter"
            className="px-2 py-1.5 border text-xs w-48"
            style={{ borderColor: "var(--ll-border)" }}
          />
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="sa-login-range"
            className="px-2 py-1.5 border text-xs bg-white"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={showDormantOnly}
              onChange={(e) => setShowDormantOnly(e.target.checked)}
              data-testid="sa-login-dormant-only"
            />
            Dormant only
          </label>
          <button onClick={load} className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1" style={{ borderColor: "var(--ll-border)" }} data-testid="sa-login-refresh">
            <RotateCw size={11} /> Refresh
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
          <Loader2 size={16} className="mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="sa-login-empty">
          No tenants match this filter.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="sa-login-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
              <th className="px-4 py-2 font-medium">Tenant</th>
              <th className="px-4 py-2 font-medium">Trend</th>
              <th className="px-4 py-2 font-medium text-right">Logins</th>
              <th className="px-4 py-2 font-medium">Last login</th>
              <th className="px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tenant_id} className="border-b hover:bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`sa-login-row-${r.tenant_id}`}>
                <td className="px-4 py-2 text-xs">
                  <div className="font-medium truncate max-w-[220px]">{r.tenant_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{r.tenant_id.slice(0, 8)}</div>
                </td>
                <td className="px-4 py-2">
                  <Sparkline values={r.daily} labels={data?.day_labels} />
                </td>
                <td className="px-4 py-2 font-mono-fin text-right text-xs">{r.total.toLocaleString()}</td>
                <td className="px-4 py-2 text-[11px] text-[var(--ll-ink-soft)]">{r.last_login ? fmtDate(r.last_login) : "—"}</td>
                <td className="px-4 py-2 text-[10px] uppercase tracking-widest">
                  {r.dormant ? (
                    <span className="px-1.5 py-0.5" style={{ background: "#FEE2E2", color: "#991B1B" }}>Dormant</span>
                  ) : (
                    <span className="px-1.5 py-0.5" style={{ background: "#DCFCE7", color: "#166534" }}>Active</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Sparkline({ values = [], labels = [] }) {
  const W = 160, H = 32, PAD = 2;
  const max = Math.max(1, ...values);
  const stepX = values.length > 1 ? (W - PAD * 2) / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - ((v / max) * (H - PAD * 2));
    return [x, y];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${(PAD + (values.length - 1) * stepX).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;
  const lastVal = values[values.length - 1] || 0;
  const tooltip = labels.length
    ? values.map((v, i) => `${labels[i]}: ${v}`).join("\n")
    : `${values.length} days`;
  return (
    <svg width={W} height={H} className="block" role="img" aria-label="Login sparkline">
      <title>{tooltip}</title>
      <path d={areaPath} fill="var(--ll-mint)" opacity="0.6" />
      <path d={linePath} fill="none" stroke="var(--ll-ink)" strokeWidth="1.5" />
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r={lastVal > 0 ? 2 : 1.5}
          fill={lastVal > 0 ? "var(--ll-ink)" : "var(--ll-ink-muted)"}
        />
      )}
    </svg>
  );
}
